import React, { useEffect, useRef, useState } from 'react';
import Icon from './icons';
import CollectPanel from './CollectPanel';
import MasterPanel from './MasterPanel';
import StepBody from './StepBody';
import RegTab from './RegTab';
import DocsTab from './DocsTab';
import RegenBanner, { masterHash } from './RegenBanner';
import { VerModal, PvModal } from './DocModals';
import { getVers } from './gendocs';
import { fileUrl, getCollectResults, uploadPreview } from './api';
import type { CollectResultRow } from './api';
import { buildCollect, mval } from './collect';
import { ensureReal } from './docgen';
import { syncCollectFromReg } from './sync';
import { stepReadiness } from './readiness';
import { isSplit, rollup, setAllStat, maxStep, SPLIT_FROM } from './costep';
import CoStatChips, { scrollToCo } from './CoStatChips';
import CoLaneMatrix from './CoLaneMatrix';
import HeadCos from './HeadCos';
import RecurStepRow from './RecurStepRow';
import { repAlert } from './report';
import { MasterChip, OfficePill } from './stepcommon';
import { ensuredMaster } from './master';
import { STEPS } from './types';
import type { Project, Tab, Collect, PersonCard } from './types';

interface Props {
  p: Project;
  tab: Tab;
  onAdvance: () => void;
  onBackStep: () => void;
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
  saveInfo?: string;
  saveError?: string;
  onRetrySave?: () => void;
}

export default function VentureDetail({ p, tab, onAdvance, onBackStep, onPatch, toast, saveInfo, saveError, onRetrySave }: Props) {
  const [masterOpen, setMasterOpen] = useState(false);
  const [ver, setVer] = useState<string | null>(null);
  const [pv, setPv] = useState<{ name: string; title?: string; url?: string } | null>(null);
  const curRef = useRef<HTMLDivElement | null>(null);
  const collectRef = useRef<Collect | null>(p.collect || null);
  collectRef.current = p.collect || null;
  const loadedCpids = useRef<Set<string>>(new Set());
  const [resultsTick, setResultsTick] = useState(0);
  // 창 포커스 복귀 시 제출 결과 재조회 — 페이지를 열어둔 사이 GP/LP가 제출한 답변 반영
  useEffect(() => {
    const onFocus = () => { loadedCpids.current.clear(); setResultsTick((t) => t + 1); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  const [viewStep, setViewStep] = useState<number | null>(null);
  const [armedAll, setArmedAll] = useState<number | null>(null);   // 「모두 완료로 표시」 두 번 누르기
  const [recurOpen, setRecurOpen] = useState(false);               // 15줄 정기·결산 보고 열람
  const rep = repAlert(p);
  useEffect(() => { if (armedAll == null) return; const t = window.setTimeout(() => setArmedAll(null), 3500); return () => window.clearTimeout(t); }, [armedAll]);
  // 레인 매트릭스에서 칸 클릭 → 그 단계를 열고 그 회사 카드로
  const openCoStep = (n: number, coId: string) => {
    setViewStep(n === p.step ? null : n);
    window.setTimeout(() => scrollToCo(coId, n), 60);
  };
  // 열람 상한은 maxStep — 8단계부터는 앞선 법인이 프로젝트 커서(가장 늦은 법인)보다 뒤 단계에 있을 수 있어
  // p.step 으로 막으면 레인 매트릭스·「클릭하여 열기」가 그 단계를 열지 못했다 (QA 2026-09-24)
  // 회사별 구간(8~15단계)은 아직 오지 않은 단계도 「미리 준비」로 연다 — 14단계 투자확인서 발급에 필요한 정보·서류를
  // VICS 보고 중에 미리 보고 모을 수 있어야 한다 (지도사 2026-09-24: "투자확인서 발급시 필요한 정보, 서류 내가 알려줬잖아").
  // 상태(대기·진행·완료)는 각 카드에서 그대로 바꿀 수 있고, 일괄 「모두 완료로 표시」만 아직 오지 않은 단계에서는 감춘다.
  // 조합 공통 구간(2~7단계)은 순서대로만 — 앞 단계 결과로 다음 서류를 만드는 구간이라 열람 상한(maxStep)을 그대로 둔다.
  const mx = maxStep(p);
  const canView = (n: number): boolean => n >= 2 && (n <= mx || isSplit(n));
  const isAhead = (n: number): boolean => n > mx;   // 아직 어느 법인도 오지 않은 단계 — 「미리 준비」
  const shown = viewStep && canView(viewStep) ? viewStep : p.step;

  // 회사별 상태(대기·진행·완료) 변경으로 프로젝트 커서(가장 늦은 법인)가 움직여도 보던 단계는 그대로 둔다
  // — 13단계에서 한 법인을 「대기」로 놓자 화면이 12단계로 튀던 것 (지도사 2026-09-24: "따로따로야, 멋대로 이동하면 안 돼").
  // 커서를 따라가는 건 이 화면의 명시적 이동(이전/다음 단계 · 타임라인 상태 버튼)뿐이다.
  const patchKeep = (patch: Partial<Project>) => {
    if (viewStep == null && ('costep' in patch || 'codone' in patch)) setViewStep(shown);
    onPatch(patch);
  };

  useEffect(() => {
    if (tab === 'progress' && curRef.current) curRef.current.scrollIntoView({ block: 'center' });
  }, [tab, shown]);

  useEffect(() => {
    if (tab !== 'progress' || p.step < 2) return;
    const patch: Partial<Project> = {};
    const c0 = p.collect ?? buildCollect(p);
    const synced = syncCollectFromReg(p, c0);
    const c = synced || c0;
    if (!p.collect || synced) patch.collect = c;
    const m = ensuredMaster(p, c);
    if (JSON.stringify(p.master) !== JSON.stringify(m)) patch.master = m;
    if (!p.mhash) patch.mhash = masterHash({ ...p, master: m, collect: c });
    if (Object.keys(patch).length) onPatch(patch);
  }, [p, tab, onPatch]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (pv) setPv(null);
      else if (ver) setVer(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pv, ver]);

  // 자료수집 제출 결과 자동 로드 — 카드별 '결과 보기'를 열지 않아도 마스터·문서 자동 채움이 되도록
  useEffect(() => {
    const c = p.collect;
    if (!c) return;
    const cards: PersonCard[] = [c.gp, ...c.lps];
    const targets = cards.filter((cd) => cd.collectProjectId && !loadedCpids.current.has(cd.collectProjectId));
    if (!targets.length) return;
    targets.forEach((cd) => loadedCpids.current.add(cd.collectProjectId as string));
    Promise.all(targets.map((cd) =>
      getCollectResults(p.id, cd.collectProjectId as string)
        .then((res) => ({ cpid: cd.collectProjectId as string, rows: res.rows }))
        .catch(() => null)
    )).then((loaded) => {
      const valid = loaded.filter(Boolean) as { cpid: string; rows: CollectResultRow[] }[];
      if (!valid.length) return;
      const base = collectRef.current;
      if (!base) return;
      const nc = JSON.parse(JSON.stringify(base)) as Collect;
      const apply = (cd: PersonCard) => {
        const f = valid.find((v) => v.cpid === cd.collectProjectId);
        if (!f) return;
        cd.results = f.rows.map((r) => ({
          title: r.title, type: r.type, required: r.required,
          sensitive: !!(cd.items.find((it) => it.title === r.title)?.sensitive),
          grp: r.section, status: r.status, value: r.value, date: '',
          uploadId: r.uploadId || (r.files[0] && r.files[0].id) || undefined,
        }));
        if (f.rows.some((r) => r.status !== 'none')) cd.sent = true;
      };
      apply(nc.gp); nc.lps.forEach(apply);
      const patch: Partial<Project> = { collect: nc };
      const cAddr = mval(nc.gp, 'orgAddr');
      if (cAddr && !(p.addr || '').trim()) patch.addr = cAddr;
      // 변경이 있을 때만 저장 — 포커스 재조회 시 불필요한 쓰기 방지
      if (patch.addr || JSON.stringify(nc) !== JSON.stringify(base)) onPatch(patch);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.collect, p.id, p.addr, onPatch, resultsTick]);

  const openPv = (k: string, idx?: number) => {
    const vers = getVers(p, k);
    const v = vers[idx == null ? vers.length - 1 : idx];
    if (v.s3pdf) {
      fileUrl(v.s3pdf, v.pdf || v.doc, true)
        .then((url) => setPv({ name: v.pdf || v.doc, url }))
        .catch((e) => { toast((e as Error).message); setPv({ name: v.pdf || v.doc }); });
      return;
    }
    if (idx != null) { setPv({ name: v.pdf || v.doc }); return; }
    // 현재 버전 미생성 → 자동 생성 후 표시
    setPv({ name: v.pdf || v.doc });
    ensureReal(p, k, onPatch, toast)
      .then((nv) => {
        if (nv.s3pdf) return fileUrl(nv.s3pdf, nv.pdf || nv.doc, true).then((url) => setPv({ name: nv.pdf || nv.doc, url }));
        return undefined;
      })
      .catch(() => undefined);
  };
  const openFilePv = (name: string, title?: string) => {
    if (!name) { toast('파일이 없습니다 (미제출)'); return; }
    const c = collectRef.current;
    let hit: { cpid: string; uploadId: string } | null = null;
    if (c) {
      const cards: PersonCard[] = [c.gp, ...c.lps];
      for (let i = 0; i < cards.length && !hit; i++) {
        const cd = cards[i];
        if (!cd.collectProjectId || !cd.results) continue;
        const r = cd.results.find((x) => x.value === name && x.uploadId);
        if (r && r.uploadId) hit = { cpid: cd.collectProjectId, uploadId: r.uploadId };
      }
    }
    if (!hit) { setPv({ name, title }); return; }
    const h = hit;
    uploadPreview(h.uploadId)
      .then((r) => setPv({ name, title, url: r.url || undefined }))
      .catch(() => setPv({ name, title }));
  };

  const head = (
    <div className="dhead">
      <h1>{p.org} <OfficePill p={p} /><span className={'pill ' + (p.done ? 'p-green' : 'p-amber')}>{p.done ? '✅ 완료' : '진행중 · ' + p.step + '/' + STEPS.length}</span>{rep ? <span className={'pill ' + (rep.stat === 'late' ? 'p-red' : 'p-amber')} style={{ marginLeft: 6, cursor: 'pointer' }} title="정기·결산 보고 열기" onClick={() => { setRecurOpen(true); window.setTimeout(() => { const el = document.getElementById('step-recur'); if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, 60); }}>📅 {rep.label}</span> : null}{saveInfo ? <span className={'pill ' + (saveError ? 'p-red' : 'p-gray')} style={{ marginLeft: 6 }} title={saveError || ''}>{saveInfo}</span> : null}
        {saveError ? <button className="rbtn danger" style={{ marginLeft: 4 }} onClick={onRetrySave}>다시 저장</button> : null}</h1>
      <div className="meta">
        <div className="m">출자총액<b className="mono">{p.total || '-'}원</b></div>
        <div className="m">GP<b>{p.gp}</b></div>
      </div>
      <div className="meta"><HeadCos p={p} /></div>
    </div>
  );

  let body: React.ReactNode = null;

  if (tab === 'progress') {
    body = (
      <div className="panel">
        <div className="panel-h"><b>{STEPS.length}단계 진행 타임라인 <span className="s3muted" style={{ fontWeight: 500 }}>+ 정기·결산 보고</span></b><span className="hs">{p.done ? '✅ 전체 완료' : '현재 ' + p.step + '단계 · ' + STEPS[p.step - 1][0]}</span></div>
        <div className="panel-b">
          <RegenBanner p={p} onPatch={onPatch} toast={toast} />
          {STEPS.map((s, i) => {
            const n = i + 1;
            const isCur = n === p.step;
            const isOpen = n === shown;
            const navigable = canView(n);
            const ahead = isAhead(n);
            const rd = n >= 2 ? stepReadiness(p, n) : null;
            const r = isOpen ? rd : null;
            const ro = isSplit(n) && rollup(p, n).total > 0 ? rollup(p, n) : null;
            const stat: 'wait' | 'cur' | 'done' = ro
              ? (ro.done === ro.total ? 'done' : (ro.done > 0 || ro.cur > 0) ? 'cur' : 'wait')
              : ((p.done || n < p.step) ? 'done' : (n === p.step ? 'cur' : 'wait'));
            const setStat = (s: 'wait' | 'cur' | 'done') => {
              if (ro) { patchKeep(setAllStat(p, n, s)); return; }
              setViewStep(null);   // 타임라인 상태 버튼은 명시적 이동 — 새 현재 단계를 연다
              if (s === 'done') onPatch(n === STEPS.length ? { step: STEPS.length, done: true } : { step: n + 1, done: false });
              else if (s === 'cur') onPatch({ step: n, done: false });
              else onPatch({ step: Math.max(1, n - 1), done: false });
            };
            const cls = stat === 'cur' ? 'cur' : stat === 'done' ? 'done' : 'todo';
            return (
              <React.Fragment key={n}>
              {n === SPLIT_FROM && p.step >= SPLIT_FROM && rollup(p, n).total > 0 ? (
                // 8단계부터 회사별로 갈라진다 — 레일을 끊지 않고 한 줄로 끼워 넣는다 (2026-09-24)
                <div className="step lanes done"><div className="rail"><div className="dot"><span>🏢</span></div><div className="line" /></div>
                  <div className="body"><CoLaneMatrix p={p} onOpen={openCoStep} onPatch={onPatch} toast={toast} /></div></div>
              ) : null}
              <div className={'step ' + cls} ref={isOpen ? curRef : undefined}>
                <div className="rail">
                  <div className="dot">{stat === 'done' ? <Icon n="check" /> : <span>{n}</span>}</div>
                  <div className="line" />
                </div>
                <div className="body">
                  <div
                    className="t"
                    style={navigable ? { cursor: 'pointer' } : undefined}
                    title={navigable ? (isOpen && !isCur ? '클릭하면 닫고 현재 단계로 돌아갑니다' : ahead ? '아직 오지 않은 단계 — 필요한 정보·서류를 미리 보고 준비합니다' : '클릭하면 이 단계를 엽니다') : undefined}
                    onClick={() => { if (navigable) setViewStep(isCur || isOpen ? null : n); }}
                  >
                    {s[0]}{' '}
                    {isCur ? <span className="pill p-amber" style={{ fontSize: 10 }}>현재</span> : null}
                    {ro ? <CoStatChips p={p} n={n} onJump={(id) => { if (!isOpen && navigable) setViewStep(n); window.setTimeout(() => scrollToCo(id, n), 60); }} /> : null}
                    {!ro && n >= 2 && n <= mx && rd ? (
                      rd.missing.length === 0
                        ? <span className="pill p-green" style={{ fontSize: 10, marginLeft: isCur ? 4 : 0 }}>완료 {rd.done}/{rd.total}</span>
                        : rd.done > 0
                          ? <span className="pill p-amber" style={{ fontSize: 10, marginLeft: isCur ? 4 : 0 }}>진행 {rd.done}/{rd.total}</span>
                          : <span className="pill p-gray" style={{ fontSize: 10, marginLeft: isCur ? 4 : 0 }}>미시작 0/{rd.total}</span>
                    ) : n === 1 && p.step > 1 ? <span className="pill p-green" style={{ fontSize: 10, marginLeft: isCur ? 4 : 0 }}>완료</span> : null}
                    {!isCur && isOpen ? <span className="pill p-blue" style={{ fontSize: 10, marginLeft: 4 }}>{ahead ? '미리 준비' : '열람 중'}</span> : null}
                    {navigable && !isOpen ? <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 11 }}> · {ahead ? '클릭하여 미리 보기' : '클릭하여 열기'}</span> : null}
                  </div>
                  <div className="d">{s[1]}</div>
                  <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 5, marginTop: 6, alignItems: 'center' }}>
                    {ro ? (
                      // 회사별 상태는 각 카드에서 바꾼다. 여기는 읽기 전용 + 일괄 동사 버튼 하나(두 번 눌러 확정)
                      // 아직 오지 않은 단계에는 일괄 완료 대신 「미리 보기」 — 14단계는 발급에 필요한 정보·서류 목록이 여기 있다
                      ahead
                        ? <button className="rbtn" title="아직 오지 않은 단계 — 필요한 정보·서류를 미리 보고 준비합니다" onClick={() => setViewStep(isOpen ? null : n)}>{isOpen ? '미리 보기 닫기' : n === 14 ? '🧾 필요 정보·서류 미리 보기' : '미리 보기'}</button>
                      : ro.done === ro.total
                        ? <span className="s3muted" style={{ fontSize: 11 }}>모든 법인 완료</span>
                        : armedAll === n
                          ? <button className="pill p-amber" style={{ border: 'none', cursor: 'pointer' }} onClick={() => { patchKeep(setAllStat(p, n, 'done')); setArmedAll(null); toast('법인 ' + ro.total + '곳을 ' + n + '단계 완료로 표시했습니다'); }}>한 번 더 누르면 {ro.total - ro.done}곳 완료 처리</button>
                          : <button className="rbtn" onClick={() => setArmedAll(n)}>모두 완료로 표시</button>
                    ) : null}
                    {ro ? null : (['wait', 'cur', 'done'] as const).map((sv) => {
                      const on = stat === sv;
                      const lbl = sv === 'wait' ? '대기' : sv === 'cur' ? '진행' : '완료';
                      const col = sv === 'wait' ? '#6B7280' : sv === 'cur' ? '#B45309' : '#15803D';
                      return (
                        <button key={sv} onClick={() => setStat(sv)} style={{ fontSize: 11, fontWeight: 600, padding: '2px 11px', borderRadius: 999, cursor: 'pointer', border: '1px solid ' + (on ? col : '#d6d9e0'), background: on ? col : '#fff', color: on ? '#fff' : '#5a6175' }}>{lbl}</button>
                      );
                    })}
                  </div>
                  {i === 0 ? (
                    <CollectPanel p={p} onPatch={onPatch} toast={toast} />
                  ) : isOpen ? (
                    <StepBody n={n} p={p} onPatch={patchKeep} toast={toast} masterOpen={masterOpen} onToggleMaster={() => setMasterOpen(!masterOpen)} onPv={openPv} onVer={setVer} onFilePv={openFilePv} />
                  ) : null}
                  {isOpen && r ? (
                    <div className="s3file" style={{ marginTop: 10 }}>
                      <span className="rf">{r.missing.length === 0
                        ? '✅ 이 단계 체크 완료 — 다음 단계로 진행해도 좋습니다'
                        : '🧭 남은 항목: ' + r.missing.slice(0, 2).join(', ') + (r.missing.length > 2 ? ' 외 ' + (r.missing.length - 2) + '건' : '')}</span>
                      <span className={'pill ' + (r.missing.length === 0 ? 'p-green' : 'p-amber')}>{r.done}/{r.total} 준비</span>
                    </div>
                  ) : null}
                  {isCur ? (
                    <div className="stepnav">
                      {p.step > 1 && (!ro || p.step === SPLIT_FROM) ? <button className="btn sm" onClick={() => { setViewStep(null); onBackStep(); }}>← 이전 단계</button> : <span />}
                      {ro
                        ? <span className="pill p-gray">회사별 상태는 각 카드의 대기·진행·완료로 — 화면은 옮기지 않고, 다 끝나면 아래 「다음 단계 열기」로</span>
                        : p.step < STEPS.length
                          ? <button className="btn pri sm" onClick={() => { if (r && r.missing.length) toast('미완료 ' + r.missing.length + '건 상태로 다음 단계로 이동했습니다'); setViewStep(null); onAdvance(); }}><Icon n="check" style={{ width: 13, height: 13 }} /> 이 단계 완료 · 다음 →</button>
                          : <span className="pill p-green">최종 단계 완료</span>}
                    </div>
                  ) : !isCur && isOpen ? (
                    <div className="stepnav">
                      <span className="pill p-gray">{ahead ? '아직 오지 않은 단계 미리 준비 · 올린 서류·입력은 자동 저장되어 단계가 열리면 그대로 쓰입니다' : (n < p.step ? '지난' : '앞선 법인의') + ' 단계 열람 · 수정 내용은 자동 저장됩니다'}</span>
                      <span style={{ display: 'flex', gap: 6 }}>
                        {ro && ro.done === ro.total && n < STEPS.length ? <button className="btn pri sm" onClick={() => setViewStep(n + 1 === p.step ? null : n + 1)}>✅ 모든 법인 완료 · {n + 1}단계 열기 →</button> : null}
                        <button className="btn sm" onClick={() => setViewStep(null)}>현재 단계로 →</button>
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
              </React.Fragment>
            );
          })}
          <RecurStepRow p={p} open={recurOpen} onToggle={() => setRecurOpen(!recurOpen)} onPatch={onPatch} toast={toast} />
        </div>
      </div>
    );
  } else if (tab === 'reg') {
    body = <RegTab p={p} onPatch={onPatch} toast={toast} />;
  } else if (tab === 'collect') {
    body = (
      <div className="panel">
        <div className="panel-h"><b>자료수집 · 1단계 기초정보 수집</b><span className="hs">업무관리 동일 · 당사자별 리스트/결과</span></div>
        <div className="panel-b">
          <div className="alert blue" style={{ marginBottom: 10 }}>
            <Icon n="info" />
            <div><b>당사자별로 항목을 편집하고 전송하세요</b><span>대표가 GP·LP인 투자사는 그 사람 줄에 자동 합산됩니다. 한 번 받은 자료는 20종 서류로 자동 전파됩니다.</span></div>
          </div>
          <CollectPanel p={p} onPatch={onPatch} toast={toast} />
        </div>
      </div>
    );
  } else if (tab === 'master') {
    body = (
      <div className="panel">
        <div className="panel-h"><b>🗂 마스터 정보</b><MasterChip m={p.master} /><span className="hs">고객 답변을 보며 입력 → 규약·결성계획서·확인서·동의서 자동 치환</span></div>
        <div className="panel-b">
          <RegenBanner p={p} onPatch={onPatch} toast={toast} />
          <div className="snote" style={{ marginBottom: 12 }}>모든 문서는 여기 마스터값으로 생성됩니다. 각 항목 옆 "고객답변"은 자료수집(1단계) 제출값이며 [↤ 가져오기]로 채울 수 있습니다.</div>
          <MasterPanel p={p} onPatch={onPatch} toast={toast} withAnswers />
        </div>
      </div>
    );
  } else {
    body = <DocsTab p={p} onPv={openPv} onVer={setVer} toast={toast} />;
  }

  return (
    <>
      {head}{body}
      {ver ? <VerModal p={p} k={ver} onPv={(k, i) => openPv(k, i)} onClose={() => setVer(null)} toast={toast} /> : null}
      {pv ? <PvModal name={pv.name} title={pv.title} url={pv.url} onClose={() => setPv(null)} /> : null}
    </>
  );
}
